import { Button, Stack } from "@mui/material";
import { type FC } from "react";

import {
  DataGridHeader,
  type IDataGridHeaderItem,
} from "~/components/UI/DataGridHeader";
import { INITIAL_MARKETS_LIMIT } from "~/feature/event/components/MarketsDataGrid/constants";
import {
  MARKETS_DATA_GRID_FIELDS,
  marketsDataGridCellByField,
} from "~/feature/event/components/MarketsDataGrid/marketsDataGridCells";
import {
  isMarketsDataGridGroupRow,
  isMarketsDataGridMarketRow,
  isMarketsDataGridNoteRow,
  MarketsDataGridField,
} from "~/feature/event/components/MarketsDataGrid/types";
import { useMarketsDataGridRows } from "~/feature/event/components/MarketsDataGrid/useMarketsDataGridRows";
import type {
  IAggregatedEvent,
  IMarket,
  IOutcomeConsensus,
} from "~/types/event";

import * as Styled from "./InitialMarketsDataGrid.styles";

export interface IInitialMarketsDataGridProps {
  event: IAggregatedEvent;
  markets: IMarket[];
  outcomes: IOutcomeConsensus[];
  /** How many markets the grid's first query will show. */
  limit: number;
  headerItems: IDataGridHeaderItem[];
}

/**
 * Server-rendered twin of `MarketsDataGrid`. `DataGridPro` renders no rows on
 * the server, so this draws the same first page from the event loader's
 * markets — same header, cells, column widths and row heights — until the
 * client-side query resolves and the real grid replaces it.
 */
export const InitialMarketsDataGrid: FC<IInitialMarketsDataGridProps> = ({
  event,
  markets,
  outcomes,
  limit,
  headerItems,
}) => {
  const rows = useMarketsDataGridRows({
    markets: markets.slice(0, limit),
    outcomes,
    isUngrouped: false,
  });

  const total = markets.length;
  const visibleMarketsCount = rows.filter(isMarketsDataGridMarketRow).length;
  // The grid only draws the thicker standalone-market border once the rows
  // actually mix groups and loose markets.
  const hasGroupRows = rows.some(isMarketsDataGridGroupRow);

  return (
    <Styled.Body data-testid="markets-initial-grid">
      <DataGridHeader items={headerItems} />
      {rows.map((row) => (
        <Styled.Row
          key={row.id}
          isGroup={!isMarketsDataGridMarketRow(row)}
          isNote={isMarketsDataGridNoteRow(row)}
          isStandalone={
            hasGroupRows && isMarketsDataGridMarketRow(row) && !row.isInGroup
          }
        >
          {(isMarketsDataGridNoteRow(row)
            ? [MarketsDataGridField.Market]
            : MARKETS_DATA_GRID_FIELDS
          ).map((field) => (
            <Styled.Cell
              key={field}
              data-field={field}
              data-row-type={row.rowType}
            >
              {marketsDataGridCellByField[field](row, event)}
            </Styled.Cell>
          ))}
        </Styled.Row>
      ))}
      <Styled.Footer>
        Total markets:{" "}
        {visibleMarketsCount < total
          ? `${visibleMarketsCount} of ${total}`
          : total}
      </Styled.Footer>
      {/* Same markup and condition as the grid's toggle. Without it the button
          appears only after the swap and pushes the page down by its height. */}
      {total > INITIAL_MARKETS_LIMIT && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button size="small" variant="text" color="info">
            Show more
          </Button>
        </Stack>
      )}
    </Styled.Body>
  );
};
